import React, { useState } from "react";
import styled from "styled-components";
import MUITabs from "@mui/material/Tabs";
import MUITab from "@mui/material/Tab";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "../Icons";
import { color, radius, shadow, fontFamily } from "../Theme";

// Tabs for the staff and client profile pages: scrollable (with scroll buttons
// on every width) plus a "More" menu that lists every tab, so nothing is hidden
// on a narrow screen. Colours and radius come from the per-brand tokens.
// Same props as Components/Input/Tabs.

const Bar = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background: ${color("surface")};
  border: 1px solid ${color("border")};
  border-radius: ${() => radius("card")}px;
  box-shadow: ${() => shadow("card")};
  font-family: ${() => fontFamily()};
  overflow: clip;
`;

const Scroller = styled.div`
  flex: 1;
  min-width: 0;
  .MuiTab-root {
    text-transform: none;
    min-width: 0;
    min-height: 48px;
    padding: 12px 16px;
    font-weight: 600;
    font-family: inherit;
    color: ${color("text-muted")};
  }
  .MuiTab-root:hover { color: ${color("primary")}; }
  .MuiTab-root.Mui-selected { color: ${color("primary")}; }
  .MuiTab-root.Mui-focusVisible { outline: 2px solid ${color("primary")}; outline-offset: -2px; }
  .MuiTabs-indicator { background-color: ${color("primary")}; height: 3px; }
  .MuiTabScrollButton-root { width: 32px; color: ${color("primary")}; }
  .MuiTabScrollButton-root.Mui-disabled { opacity: 0.3; }
`;

const MoreButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 48px;
  padding: 0 12px;
  border: 0;
  border-left: 1px solid ${color("border")};
  background: transparent;
  color: ${color("primary")};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${color("list")}; }
  &:focus-visible { outline: 2px solid ${color("primary")}; outline-offset: -2px; }
  svg { color: inherit !important; }
`;

export default function ProfileTabs({ $onChanged, $value, $tabs }) {
  const [anchor, setAnchor] = useState(null);

  return (
    <Bar>
      <Scroller>
        <MUITabs
          value={$value}
          onChange={(event, newValue) => $onChanged(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Profile sections"
          TabIndicatorProps={{ style: { backgroundColor: color("primary") } }}
        >
          {$tabs?.map((tab, index) => (
            <MUITab key={tab?.name ?? index} disableRipple label={tab?.name} />
          ))}
        </MUITabs>
      </Scroller>
      <MoreButton
        type="button"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        More
        <Icon $icon="ArrowDropDown" $size={20} />
      </MoreButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {$tabs?.map((tab, index) => (
          <MenuItem
            key={tab?.name ?? index}
            selected={index === $value}
            onClick={() => {
              setAnchor(null);
              $onChanged(index);
            }}
          >
            {tab?.name}
          </MenuItem>
        ))}
      </Menu>
    </Bar>
  );
}
